import type { Rng } from "../rng";
import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	type PlayerIndex,
	reject,
} from "../types";

export const SLIDE_SIZE = 3;
const CELLS = SLIDE_SIZE * SLIDE_SIZE;
/** The blank square. */
export const BLANK = 0;

export interface SlidingState extends BaseState {
	kind: "sliding";
	/** One board each — you race on your own, side by side. */
	boards: [number[], number[]];
	moves: [number, number];
}

export interface SlidingMove {
	tile: number;
}

const solved = (): number[] => [...Array(CELLS - 1).keys()].map((n) => n + 1).concat(BLANK);

function neighbours(index: number): number[] {
	const col = index % SLIDE_SIZE;
	const row = Math.floor(index / SLIDE_SIZE);
	const list: number[] = [];
	if (col > 0) list.push(index - 1);
	if (col < SLIDE_SIZE - 1) list.push(index + 1);
	if (row > 0) list.push(index - SLIDE_SIZE);
	if (row < SLIDE_SIZE - 1) list.push(index + SLIDE_SIZE);
	return list;
}

/**
 * How far a dealt board must be from solved, as summed Manhattan distance.
 *
 * A random walk can wander home: sixty unrestricted steps dealt an
 * already-solved board once in three hundred games, and a nearly-solved one
 * often enough that some races were over in three taps. Ten tile-distances is
 * at least ten real moves, which is a race rather than a formality.
 */
const MIN_SCRAMBLE_DISTANCE = 10;

/** How much each tile is out of place, summed — a lower bound on moves left. */
function scrambleDistance(board: readonly number[]): number {
	let distance = 0;
	for (let index = 0; index < CELLS; index++) {
		const tile = board[index];
		if (tile === BLANK) continue;
		const goal = tile - 1;
		distance +=
			Math.abs((index % SLIDE_SIZE) - (goal % SLIDE_SIZE)) +
			Math.abs(Math.floor(index / SLIDE_SIZE) - Math.floor(goal / SLIDE_SIZE));
	}
	return distance;
}

/** Scrambles by making real moves, so the puzzle is always solvable. */
function scramble(rng: Rng, steps = 60): number[] {
	const board = solved();
	let blank = board.indexOf(BLANK);

	const walk = (count: number) => {
		for (let i = 0; i < count; i++) {
			const options = neighbours(blank);
			const pick = options[rng.int(options.length)];
			board[blank] = board[pick];
			board[pick] = BLANK;
			blank = pick;
		}
	};

	walk(steps);
	// Keep walking in short bursts until the board is genuinely scrambled. The
	// cap keeps this terminating and deterministic; it is never reached in
	// practice — a bounded walk that stays near home for hundreds of steps is
	// not a thing a random walk does.
	for (let burst = 0; burst < 30 && scrambleDistance(board) < MIN_SCRAMBLE_DISTANCE; burst++) {
		walk(12);
	}
	return board;
}

export function isSolved(board: number[]): boolean {
	return board.every((tile, index) => tile === solved()[index]);
}

export const slidingRace: GameDefinition<SlidingState, SlidingMove> = {
	id: "sliding",
	category: "puzzle",
	title: "Sliding Puzzle Race",
	emoji: "🧩",
	blurb: "Same scrambled puzzle each. First to put it in order wins.",
	howTo: [
		"You each get the same scrambled puzzle.",
		"Tap a tile next to the gap to slide it across.",
		"Put the numbers back in order, 1 to 8.",
		"No turns — just go as fast as you can!",
	],

	create(rng) {
		// The same scramble for both, so it is a fair race.
		const start = scramble(rng);
		return {
			kind: "sliding",
			boards: [[...start], [...start]],
			moves: [0, 0],
			// Both players work on their own board at the same time.
			turn: null,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const tile = (raw as { tile?: unknown }).tile;
		if (typeof tile !== "number" || !Number.isInteger(tile)) return null;
		if (tile < 0 || tile >= CELLS) return null;
		return { tile };
	},

	apply(state, player, move): MoveOutcome<SlidingState> {
		if (state.result) return reject("This game is already finished.");

		const board = [...state.boards[player]];
		const blank = board.indexOf(BLANK);
		if (!neighbours(blank).includes(move.tile)) {
			return reject("That tile isn't next to the gap.");
		}

		board[blank] = board[move.tile];
		board[move.tile] = BLANK;

		const boards: [number[], number[]] = [[...state.boards[0]], [...state.boards[1]]];
		boards[player] = board;
		const moves: [number, number] = [...state.moves];
		moves[player] += 1;

		const done = isSolved(board);
		const scores: [number, number] = [...state.scores];
		if (done) scores[player] = 1;

		return {
			ok: true,
			state: {
				...state,
				boards,
				moves,
				scores,
				turn: null,
				result: done ? { winner: player } : null,
			},
		};
	},

	legalMoves(state, player) {
		if (state.result) return [];
		const blank = state.boards[player].indexOf(BLANK);
		return neighbours(blank).map((tile) => ({ tile }));
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},

	redact(state, viewer) {
		// You can see how many moves they've made, but not their board.
		const other: PlayerIndex = viewer === 0 ? 1 : 0;
		const boards: [number[], number[]] = [[], []];
		boards[viewer] = state.boards[viewer];
		boards[other] = [];
		return { ...state, boards };
	},
};
